import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  ShieldCheckIcon,
  LockClosedIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  LockOpenIcon
} from '@heroicons/react/24/outline';
import fintechLoginBg from '../assets/fintech_login_bg.png';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(username, password, twoFactorCode || null);
    } catch (err) {
      if (err.response?.data?.code === '2FA_REQUIRED') {
        setRequires2FA(true);
      } else {
        setError(err.response?.data?.message || 'Authentication failed. Please verify credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[#090b11] text-slate-100 overflow-hidden font-sans relative">
      {/* Background Decorative Glowing Blobs */}
      <div className="absolute top-[-10%] right-[-10%] w-[45vw] h-[45vw] rounded-full bg-indigo-600/10 blur-[150px] animate-drift pointer-events-none z-0" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[45vw] h-[45vw] rounded-full bg-cyan-500/10 blur-[150px] animate-drift-reverse pointer-events-none z-0" />

      {/* Split Screen Left Side: Splash Banner */}
      <div className="hidden lg:flex lg:w-[48%] xl:w-[52%] relative flex-col justify-between p-12 overflow-hidden border-r border-slate-900/60 z-10">
        {/* Futuristic Image Background with Dark Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-10000 hover:scale-105"
          style={{ backgroundImage: `url(${fintechLoginBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-900/40 mix-blend-multiply" />
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-950/20 via-transparent to-slate-950/80" />

        {/* Top Branding Header */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-10 w-10 flex items-center justify-center rounded-xl bg-gradient-to-tr from-indigo-500 to-cyan-400 p-[1.5px] shadow-[0_0_15px_rgba(99,102,241,0.4)]">
            <div className="h-full w-full bg-slate-950 rounded-[10px] flex items-center justify-center">
              <ShieldCheckIcon className="h-6 w-6 text-indigo-400" />
            </div>
          </div>
          <div>
            <span className="text-xl font-bold tracking-tight text-white">Xpert<span className="text-indigo-400">Payments</span></span>
            <div className="text-[9px] uppercase tracking-widest text-slate-400 -mt-1 font-semibold">Enterprise Suite</div>
          </div>
        </div>

        {/* Center Marketing Copy */}
        <div className="relative z-10 my-auto max-w-lg space-y-8">
          <div className="space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-indigo-500/10 border border-indigo-500/30 text-indigo-300">
              <span className="h-2 w-2 rounded-full bg-indigo-400 animate-pulse" />
              PCI DSS Level 1 Certified
            </span>
            <h1 className="text-4xl xl:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Next-Gen <br />
              <span className="bg-gradient-to-r from-indigo-400 via-indigo-300 to-cyan-300 bg-clip-text text-transparent">
                QR Payment Gateway
              </span>
            </h1>
            <p className="text-slate-300 text-base leading-relaxed">
              Empower your business with high-throughput QR billing, automated multi-bank reconciliation, and dual-layered PCI-compliant security guards.
            </p>
          </div>

          {/* Key Value Checklist */}
          <div className="space-y-4 border-t border-slate-800/80 pt-6">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-indigo-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-white">Direct Core Banking API Integration</h4>
                <p className="text-xs text-slate-400">Low-latency webhooks for instant secure settlement notifications.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-indigo-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-white">Automated Reconciliation Engine</h4>
                <p className="text-xs text-slate-400">Match payments to bills without human error using adaptive rules.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-indigo-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-white">Omnichannel Dispatch</h4>
                <p className="text-xs text-slate-400">Instantly notify clients via secure WhatsApp, SMS, or rich email links.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div className="relative z-10 flex items-center justify-between text-xs text-slate-500 border-t border-slate-900/60 pt-4">
          <p>© 2026 XpertPayments. All rights reserved.</p>
          <p className="flex items-center gap-1">
            <LockClosedIcon className="h-3 w-3" />
            256-bit Encryption
          </p>
        </div>
      </div>

      {/* Split Screen Right Side: Interactive Card & Login Portal */}
      <div className="w-full lg:w-[52%] xl:w-[48%] flex flex-col justify-center items-center p-6 sm:p-12 md:p-16 z-10 relative">

        {/* Minimal Mobile Branding Header */}
        <div className="lg:hidden absolute top-8 left-8 flex items-center gap-2">
          <ShieldCheckIcon className="h-6 w-6 text-indigo-400" />
          <span className="text-lg font-bold tracking-tight text-white">Xpert<span className="text-indigo-400">Payments</span></span>
        </div>

        <div className="w-full max-w-md relative">
          {/* Subtle Outer Card Glow Border */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500/20 to-cyan-500/20 rounded-2xl blur-md opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 pointer-events-none" />

          {/* Form Card */}
          <div className="relative bg-[#0d121f]/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-8 sm:p-10 shadow-2xl overflow-hidden">
            {/* Top Glowing Edge Accent */}
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-indigo-500/50 to-cyan-400/30" />

            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-white tracking-tight">
                Portal Sign In
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                Enter your administrative credentials to continue
              </p>
            </div>

            {error && (
              <div className="mb-6 rounded-xl bg-red-950/40 border border-red-800/50 p-4 animate-[shake_0.4s_ease-in-out]">
                <div className="flex gap-2">
                  <LockClosedIcon className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-red-300">{error}</p>
                </div>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>

              {/* Username Input */}
              <div className="space-y-2">
                <label htmlFor="username" className="block text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-slate-500" />
                  </div>
                  <input
                    id="username"
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-slate-950/40 border border-slate-800/80 rounded-xl py-3 pl-11 pr-4 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-indigo-500/80 focus:ring-2 focus:ring-indigo-500/10 focus:bg-slate-950/70 transition-all duration-300"
                    placeholder="Enter your username"
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label htmlFor="password" className="block text-xs font-semibold text-slate-400 uppercase tracking-widest">
                    Password
                  </label>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-slate-500" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-slate-950/40 border border-slate-800/80 rounded-xl py-3 pl-11 pr-11 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-indigo-500/80 focus:ring-2 focus:ring-indigo-500/10 focus:bg-slate-950/70 transition-all duration-300"
                    placeholder="••••••••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* 2FA Input (Fades/Animates In if required) */}
              {requires2FA && (
                <div className="space-y-2 pt-2 border-t border-slate-800/60 animate-[fadeIn_0.3s_ease-out]">
                  <div className="flex items-center gap-1.5 mb-1 text-amber-400">
                    <LockOpenIcon className="h-4 w-4" />
                    <label htmlFor="2fa" className="block text-xs font-semibold uppercase tracking-widest">
                      Two-Factor Authentication Code
                    </label>
                  </div>
                  <input
                    id="2fa"
                    type="text"
                    required
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    className="w-full bg-slate-950/60 border border-amber-500/40 rounded-xl py-3 px-4 text-center tracking-[0.5em] font-mono text-lg text-amber-300 placeholder-slate-700 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/10 transition-all duration-300"
                    placeholder="000000"
                    maxLength={6}
                  />
                  <p className="text-[10px] text-slate-400 text-center">
                    Enter the verification code generated by your authenticator app
                  </p>
                </div>
              )}

              {/* Action Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 px-4 bg-gradient-to-r from-indigo-600 via-indigo-500 to-cyan-500 hover:from-indigo-500 hover:via-indigo-400 hover:to-cyan-400 text-white font-semibold rounded-xl shadow-[0_0_20px_rgba(99,102,241,0.25)] hover:shadow-[0_0_25px_rgba(99,102,241,0.45)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transform active:scale-[0.98] outline-none focus:ring-2 focus:ring-indigo-400/50"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in securely...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRightIcon className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            {/* Support/Footer Info */}
            <div className="mt-8 text-center border-t border-slate-900 pt-6">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-900 border border-slate-800 text-slate-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                PCI DSS Compliant System v1.1.0
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
